import React from "react";

import { MedicalPlanMapping } from "Features/MedicalPlanMappings/medicalPlanMappings.types";
import { PlanSuiteCurrentCosts, PlanSuiteRenewalCosts, WithPlans } from "Features/PlanSuites/planSuites.types";
import { useHistory } from "react-router-dom";
import { selectRenewalSuite } from "RenewalSuitesComparison/api";
import { useRenewalSuitesComparisonContext } from "RenewalSuitesComparison/Context";
import { useCurrentAndRenewalSuites } from "RenewalSuitesComparison/hooks";
import { detailedBenefitsComparisonURL, detailedCostsComparisonURL } from "RenewalSuitesComparison/urls";
import formatCurrency from "Utils/formatCurrency";
import { MedicalPlan } from "Utils/types";
import { BasicLoading } from "ZeroRuby";

import { Collapse } from "Cobalt/Collapse";
import { ErrorHandler } from "Cobalt/ErrorHandler";
import { TitleSmall } from "Cobalt/Header";
import { CarrierLogo } from "Common/CarrierLogo";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import BodyText from "Theme/BodyText";
import Button, { LinkButton } from "Theme/Button";
import { Card, CardSection } from "Theme/Card";

interface PlanBenefitsProps {
  plan: MedicalPlan;
  isOpen: boolean;
  toggle: () => void;
  children?: React.ReactNode;
}

const PlanBenefits = ({ children, plan, isOpen, toggle }: PlanBenefitsProps) => {
  const { deductible, display_name, estimated_costs } = plan;
  const { pcp_visit, generic_drugs, urgent_care } = estimated_costs || {};

  return (
    <React.Fragment key={plan.id}>
      <Div my={3} onClick={toggle} className="flex flex-row flex-nowrap items-center justify-center cursor-pointer" aria-hidden="true">
        <TitleSmall mt={0} mb={0} className="text-ellipsis" title={display_name}>
          {display_name}
        </TitleSmall>
        <Button color="ghost" title="View Plan Benefits" px={2}>
          <Icon icon={isOpen ? "chevron-up" : "chevron-down"} />
        </Button>
      </Div>
      <Collapse key={plan.id} in={isOpen}>
        <div>
          <BodyText size="body2" mt={3}>
            Deductible
          </BodyText>
          <TitleSmall className="text-ellipsis">{formatCurrency(deductible)}</TitleSmall>
          <BodyText size="body2" mt={3}>
            Primary Care Provider
          </BodyText>
          <TitleSmall className="text-ellipsis">{formatCurrency(pcp_visit)}</TitleSmall>
          <BodyText size="body2" mt={3}>
            Generic Drugs
          </BodyText>
          <TitleSmall className="text-ellipsis">{formatCurrency(generic_drugs)}</TitleSmall>
          <BodyText size="body2" mt={3}>
            Urgent Care
          </BodyText>
          <TitleSmall className="text-ellipsis">{formatCurrency(urgent_care)}</TitleSmall>
          {children}
        </div>
      </Collapse>
    </React.Fragment>
  );
};

interface PlanSuiteCardProps {
  children?: React.ReactNode;
  isRenewal: boolean;
  medicalPlanMappings: MedicalPlanMapping[];
  planSuite: (PlanSuiteCurrentCosts & WithPlans) | (PlanSuiteRenewalCosts & WithPlans);
  planSuiteCosts: PlanSuiteCurrentCosts["currentPlanSuiteCosts"] | PlanSuiteRenewalCosts["renewalPlanSuiteCosts"];
  openMappingCollapse: boolean[];
  toggleMappingCollapse: (index: number) => void;
}

const PlanSuiteCard = (props: PlanSuiteCardProps) => {
  const history = useHistory();
  const { setSelectedMapping } = useRenewalSuitesComparisonContext();
  const { children, isRenewal, medicalPlanMappings, planSuite, planSuiteCosts, openMappingCollapse, toggleMappingCollapse } = props;
  const { plans, shortName } = planSuite;
  const { companyContribution, employeeDeduction, premium } = planSuiteCosts;
  return (
    <Card mb={0} className="flex-1 flex flex-col justify-start min-w-0" data-testid={shortName}>
      <CarrierLogo plan={plans[0]} maxSize={4} className="grayscale" />
      <TitleSmall my={4} className="text-ellipsis" title={shortName}>
        {shortName}
      </TitleSmall>
      <BodyText size="body2" mt={3}>
        Monthly Premium
      </BodyText>
      <TitleSmall className="text-ellipsis">{formatCurrency(premium)}</TitleSmall>
      <BodyText size="body2" mt={3}>
        Company Costs
      </BodyText>
      <TitleSmall className="text-ellipsis">{formatCurrency(companyContribution)}</TitleSmall>
      <BodyText size="body2" mt={3}>
        Employee Costs
      </BodyText>
      <TitleSmall className="text-ellipsis">{formatCurrency(employeeDeduction)}</TitleSmall>
      <LinkButton
        onClick={() => {
          history.push(detailedCostsComparisonURL);
        }}
        color="link"
        role="link"
        mt={3}
        mx={0}
        px={0}
        className="self-start mb-auto"
      >
        Cost Breakdown
      </LinkButton>
      {children}
      <TitleSmall className="text-ellipsis">Plans</TitleSmall>
      {medicalPlanMappings.map((mapping, index) => {
        let { planID } = mapping;
        if (isRenewal) {
          planID = mapping.items.find(({ planSuiteID }) => planSuiteID === planSuite.id)?.planID;
        }
        const plan = plans.find(({ id }) => id === planID);
        return (
          <PlanBenefits key={plan.id} plan={plan} isOpen={openMappingCollapse[index]} toggle={() => toggleMappingCollapse(index)}>
            {isRenewal ? (
              <LinkButton
                onClick={() => {
                  setSelectedMapping(medicalPlanMappings[index]);
                  history.push(detailedBenefitsComparisonURL);
                }}
                color="link"
                role="link"
                my={0}
                mx={0}
                px={0}
                className="self-start"
              >
                All Benefits
              </LinkButton>
            ) : (
              <BodyText p={2}>&nbsp;</BodyText>
            )}
          </PlanBenefits>
        );
      })}
    </Card>
  );
};

interface PlanSuiteOverviewsProps {
  medicalPlanMappings: MedicalPlanMapping[];
}

const PlanSuiteOverviews = ({ medicalPlanMappings }: PlanSuiteOverviewsProps) => {
  const [openMappingCollapse, setOpenMappingCollapse] = React.useState(medicalPlanMappings.map((_) => false));
  const toggleMappingCollapse = (index: number) => {
    const temp = [...openMappingCollapse];
    temp[index] = !temp[index];
    setOpenMappingCollapse(temp);
  };
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<Error | null>(null);
  const selectRenewalSuiteFn = async (planSuiteID: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await selectRenewalSuite({ planSuiteID });
      if (response.ok) {
        const { redirect_url } = await response.json();
        window.location.href = redirect_url;
      } else {
        const { error } = await response.json();
        setSubmitError(new Error(error));
        setSubmitting(false);
      }
    } catch (error) {
      setSubmitError(error as Error);
      setSubmitting(false);
    }
  };
  const { currentSuite, error, isError, isLoading, renewalSuites } = useCurrentAndRenewalSuites({ medicalPlanMapping: medicalPlanMappings[0] });
  if (isLoading) {
    return <BasicLoading />;
  }

  if (isError) {
    return (
      <CardSection>
        <ErrorHandler name="RenewalSuitesComparison" action="Get current plan suite" isError={isError} error={error} />
      </CardSection>
    );
  }
  return (
    <>
      <div className="flex gap-4">
        <div className="flex-1">
          <TitleSmall mb={4}>Current Plans</TitleSmall>
        </div>
        <div style={{ flexGrow: renewalSuites.length, flexShrink: 1, flexBasis: "0%" }}>
          <TitleSmall mb={4}>Renewal Options</TitleSmall>
        </div>
      </div>
      {submitError && <ErrorHandler name="RenewalSuitesComparison" action="Select renewal suite" isError={!!submitError} error={submitError} />}
      <div className="flex gap-4 items-stretch">
        <PlanSuiteCard
          isRenewal={false}
          medicalPlanMappings={medicalPlanMappings}
          openMappingCollapse={openMappingCollapse}
          planSuite={currentSuite}
          planSuiteCosts={currentSuite.currentPlanSuiteCosts}
          toggleMappingCollapse={toggleMappingCollapse}
        />
        {renewalSuites.map((planSuite) => (
          <PlanSuiteCard
            key={planSuite.id}
            isRenewal
            medicalPlanMappings={medicalPlanMappings}
            openMappingCollapse={openMappingCollapse}
            planSuite={planSuite}
            planSuiteCosts={planSuite.renewalPlanSuiteCosts}
            toggleMappingCollapse={toggleMappingCollapse}
          >
            <Button onClick={() => selectRenewalSuiteFn(planSuite.id)} mt={4} mb={4} size="large" submitting={submitting} disabled={submitting} fullWidth>
              Select Package
            </Button>
          </PlanSuiteCard>
        ))}
      </div>
    </>
  );
};

export default PlanSuiteOverviews;
